import { type HomeChatGuidanceCard } from './types';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { GpcRouterResponse } from './gpcHandoff';
import type { GpcChatSessionView } from '../GpcChat/types';
import type { MrChatSessionView } from '../MrChat/types';
import type { ConstructionSowChatSessionView } from '../ConstructionSowChat/types';
import type { ServicesPwsChatSessionView } from '../ServicesPwsChat/types';
import { clarificationSoundIdFromEnvelope, dispatchHomeChatLlmResultSound, getInAppCompletionSoundStatus, HOME_CHAT_COMPLETION_SOUND_STATUS_EVENT, tryInAppCompletionSound, type CompletionSoundStatus } from './completionNotifications';
import { HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY } from './HomeChatCompletionReconciler';
import type { Attachment, ChatMessage, ChatSession, HomeChatCard } from './types';
import { normalizeHomeChatLlmResultEvents } from './types';
import type { Turn, ClarificationEnvelope, TopicClarificationEnvelope } from '../FarChat/types';
import { isClarificationEnvelope } from '../FarChat/types';
import { Notice, PendingIntentChoiceSelection, HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { mergeHomeChatConversation, visibleHomeChatCards } from './homeChatConversation';
import type { WorkflowDocumentHistoryState } from './workflowDocumentHistory';


type Inputs = Required<Pick<Props, 'constrainedWorkflowEntry' | 'initialConstructionSowChatView' | 'initialGpcRouter' | 'initialMrChatView' | 'initialMrNotificationBoundResumeId' | 'initialMrObserverNotificationId' | 'initialSessionId' | 'initialWorkflowOnlyChoice' | 'initialWorkflowOnlyGuidance' | 'initialWorkflowOnlyMessage' | 'initialWorkflowOnlyPurchaseGuidance' | 'workflowOnly'>> &
Required<Pick<Props, 'displayMode' | 'enableCompletionSound'>>;

export function useHomeChatPanelState({
  displayMode,
  enableCompletionSound,
  constrainedWorkflowEntry,
  initialConstructionSowChatView,
  initialGpcRouter,
  initialMrChatView,
  initialMrNotificationBoundResumeId,
  initialMrObserverNotificationId,
  initialSessionId,
  initialWorkflowOnlyChoice,
  initialWorkflowOnlyGuidance,
  initialWorkflowOnlyMessage,
  initialWorkflowOnlyPurchaseGuidance,
  workflowOnly,
}: Inputs) {
  const isCompanion = displayMode === 'companion';
  const isMainHomeChatSoundSurface = enableCompletionSound && !isCompanion;
  const composerPlaceholder = isCompanion
    ? 'Ask about the FAR or this workflow stage\u2026'
    : 'Ask about the FAR or add details to this conversation\u2026';
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
  const [mobileSessionsOpen, setMobileSessionsOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  // A constrained entry allocates a real Home Chat id before its typed route
  // has persisted a controller/card. Keep that real, zero-turn row in the
  // standard rail through the list/route race; it is never a generic saved
  // chat and is eligible only while it remains the active typed entry.
  const [constrainedOptimisticSessionId, setConstrainedOptimisticSessionId] = useState<string | null>(null);
  const [gpcChatView, setGpcChatView] = useState<GpcChatSessionView | null>(null);
  const [mrChatView, setMrChatView] = useState<MrChatSessionView | null>(initialMrChatView);
  // This opaque handle is retained only while the panel is an observer. A
  // successful notification-bound Resume promotes this same private binding
  // and must switch polling back to the controller's ordinary progress path.
  const [mrObserverNotificationId, setMrObserverNotificationId] = useState<string | null>(
    initialMrObserverNotificationId,
  );
  const [mrNotificationBoundResumeId, setMrNotificationBoundResumeId] = useState<string | null>(
    initialMrNotificationBoundResumeId,
  );
  const [mrTurnInFlight, setMrTurnInFlight] = useState<{
    homeSessionId: string;
    requestId: string;
  } | null>(null);
  const [constructionSowChatView, setConstructionSowChatView] =
    useState<ConstructionSowChatSessionView | null>(initialConstructionSowChatView);
  const [workflowDocumentHistory, setWorkflowDocumentHistory] = useState<WorkflowDocumentHistoryState | null>(null);
  const [servicesPwsChatView, setServicesPwsChatView] =
    useState<ServicesPwsChatSessionView | null>(null);
  // Keep an already-hydrated GPC view visible during a repeat selection in
  // this mounted chat shell. The next `getThread` response remains
  // authoritative and replaces this display cache.
  const gpcViewsByHomeSessionRef = useRef<Map<string, GpcChatSessionView>>(new Map());
  const [rehydratingGpcSessionId, setRehydratingGpcSessionId] = useState<string | null>(null);
  // The confirmed opening purchase statement is safe to show immediately,
  // while the backend creates/binds the authoritative controller session.
  const [optimisticGpcOpening, setOptimisticGpcOpening] = useState<string | null>(null);
  const [pendingGpcRouter, setPendingGpcRouter] = useState<
    Extract<GpcRouterResponse, { kind: 'start_confirmation' | 'ambiguous_choice' | 'resume_selection' }> | null
  >(initialGpcRouter && (initialGpcRouter.kind === 'start_confirmation' || initialGpcRouter.kind === 'ambiguous_choice' || initialGpcRouter.kind === 'resume_selection') ? initialGpcRouter : null);
  const [workflowOnlyChoice, setWorkflowOnlyChoice] = useState<Record<string, unknown> | null>(initialWorkflowOnlyChoice);
  const [workflowOnlyPurchaseGuidance, setWorkflowOnlyPurchaseGuidance] = useState<HomeChatGuidanceCard | null>(initialWorkflowOnlyPurchaseGuidance);
  const [failedPurchaseFollowup, setFailedPurchaseFollowup] = useState<{ message: string; requestId: string } | null>(null);
  const [newPurchaseGuidanceFocusId, setNewPurchaseGuidanceFocusId] = useState<string | null>(
    initialWorkflowOnlyPurchaseGuidance?.card_id || null,
  );
  const lastInitialPurchaseGuidanceId = useRef(initialWorkflowOnlyPurchaseGuidance?.card_id || null);
  const [workflowOnlyGuidance, setWorkflowOnlyGuidance] = useState<string | null>(initialWorkflowOnlyGuidance);
  const [workflowOnlyEntryMessage, setWorkflowOnlyEntryMessage] = useState<string | null>(initialWorkflowOnlyMessage);

  // A constrained typed route starts only after the session is created, so
  // the panel is intentionally mounted before its controller/card response
  // arrives.  These are server-issued projections; adopt only non-null
  // values and never manufacture a controller/card from ordinary history.
  useLayoutEffect(() => {
    if (!workflowOnly) return;
    if (initialMrChatView) setMrChatView(initialMrChatView);
    if (initialConstructionSowChatView) {
      setConstructionSowChatView(initialConstructionSowChatView);
    }
    if (
      initialGpcRouter
      && (initialGpcRouter.kind === 'start_confirmation'
        || initialGpcRouter.kind === 'ambiguous_choice'
        || initialGpcRouter.kind === 'resume_selection')
    ) {
      setPendingGpcRouter(initialGpcRouter);
    }
    if (initialWorkflowOnlyChoice) setWorkflowOnlyChoice(initialWorkflowOnlyChoice);
    if (initialWorkflowOnlyPurchaseGuidance) {
      setWorkflowOnlyPurchaseGuidance(initialWorkflowOnlyPurchaseGuidance);
      if (lastInitialPurchaseGuidanceId.current !== initialWorkflowOnlyPurchaseGuidance.card_id) {
        setNewPurchaseGuidanceFocusId(initialWorkflowOnlyPurchaseGuidance.card_id);
      }
      lastInitialPurchaseGuidanceId.current = initialWorkflowOnlyPurchaseGuidance.card_id;
    }
    if (initialWorkflowOnlyGuidance) setWorkflowOnlyGuidance(initialWorkflowOnlyGuidance);
    if (initialWorkflowOnlyMessage) setWorkflowOnlyEntryMessage(initialWorkflowOnlyMessage);
  }, [
    initialConstructionSowChatView,
    initialGpcRouter,
    initialMrChatView,
    initialWorkflowOnlyChoice,
    initialWorkflowOnlyPurchaseGuidance,
    initialWorkflowOnlyGuidance,
    initialWorkflowOnlyMessage,
    workflowOnly,
  ]);
  // Live mirror of `activeSessionId` for `refreshSessions`'s active-session
  // merge. The server filters zero-turn sessions out of GET /sessions (the
  // orphan-session suppression), so a mid-flight new-chat session — which
  // has no persisted turn yet — would VANISH from the rail on any re-fetch.
  // `refreshSessions` reads this ref to KEEP the active entry (and the
  // PENDING placeholder) visible regardless of the server filter, so the
  // "New Chat" row never flashes/disappears between submit and answer. Kept
  // in a ref (not the state closure) so the merge always sees the latest id
  // without re-creating `refreshSessions` (whose identity many callbacks
  // depend on). Synced via an effect: every `refreshSessions()` is invoked
  // AFTER a state update settles, so the effect has flushed by the time the
  // async `listSessions()` resolves and the merge reads the ref.
  const activeSessionIdRef = useRef<string | null>(null);
  const reportedSessionIdRef = useRef<string | null>(null);
  // Live mirror of `sessions` so `refreshSessions` (a stable useCallback)
  // can tell an INITIAL empty load (show the loading skeleton) apart from a
  // background refresh of an already-populated rail (keep the list visible,
  // reconcile in place — no skeleton, no flash). Synced via an effect below;
  // reading the state closure here would pin `refreshSessions` to a stale
  // snapshot and force its identity to change on every list update.
  const sessionsRef = useRef<ChatSession[]>([]);
  // Each session-list response is authoritative only until a newer list
  // request begins. This prevents a slow, older poll from repainting a
  // "Working" marker that a newer response already cleared.
  const sessionListRefreshVersionRef = useRef(0);

  // `createSession()` has already issued this id, but `GET /sessions` hides
  // its zero-turn row until the constrained route writes its durable typed
  // projection. Adopt it synchronously, before the initial list effect, so
  // the ordinary rail can preserve a selected "New Chat" row instead of
  // flashing its empty state. The special row is admitted below only for this
  // active typed-entry transition; no unclassified history is exposed.
  useLayoutEffect(() => {
    if (!workflowOnly || !initialSessionId || !constrainedWorkflowEntry) return;
    const sessionId = initialSessionId;
    activeSessionIdRef.current = sessionId;
    setActiveSessionId((current) => current === sessionId ? current : sessionId);
    setConstrainedOptimisticSessionId(sessionId);
    setSessions((current) => {
      if (current.some((session) => session.session_id === sessionId)) return current;
      const nowIso = new Date().toISOString();
      return [{
        session_id: sessionId,
        title: null,
        created_at: nowIso,
        last_activity_at: nowIso,
        message_count: 0,
      }, ...current];
    });
  }, [constrainedWorkflowEntry, initialSessionId, workflowOnly]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // v2 turn state. Each successful /v2/turn response is appended here as
  // a `Turn` and rendered through `FarChatResponse`. `messages` is retained
  // solely to render historical legacy transcripts after rehydration; new
  // Home Chat sends never fall back to a v1 endpoint.
  const [turns, setTurns] = useState<Turn[]>([]);
  // Typed Home Chat cards are persisted independently of legacy v1 messages
  // and v2 turns so an old thread can safely rehydrate them after reload.
  const [chatCards, setChatCards] = useState<HomeChatCard[]>([]);
  const [busySimilarRequirementsCardId, setBusySimilarRequirementsCardId] =
    useState<string | null>(null);
  const [busyWorkflowRecommendationCardId, setBusyWorkflowRecommendationCardId] =
    useState<string | null>(null);
  const [busyActionChoiceLabel, setBusyActionChoiceLabel] = useState<string | null>(null);
  const [busyTrainingActionId, setBusyTrainingActionId] = useState<string | null>(null);
  const workflowRecommendationRequestIdRef = useRef<string | null>(null);
  const cardActionRevisionRef = useRef(0);
  // A card action is acknowledged locally before its HTTP response arrives.
  // This hides the selector synchronously and projects the selected route's
  // real pending surface. The request id is also the duplicate-click and
  // stale-response owner; a late selection must not resurrect a selector in a
  // different session.
  const [pendingIntentChoiceSelection, setPendingIntentChoiceSelection] = useState<PendingIntentChoiceSelection | null>(null);
  const pendingIntentChoiceRequestIdRef = useRef<string | null>(null);
  const clearPendingIntentChoiceSelection = useCallback((requestId?: string) => {
    if (requestId && pendingIntentChoiceRequestIdRef.current !== requestId) return;
    pendingIntentChoiceRequestIdRef.current = null;
    setPendingIntentChoiceSelection(null);
  }, []);
  const conversationItems = useMemo(
    () => mergeHomeChatConversation(messages, visibleHomeChatCards(chatCards), turns),
    [messages, chatCards, turns]
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [inFlight, setInFlight] = useState<boolean>(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return window.localStorage.getItem(HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [completionSoundStatus, setCompletionSoundStatus] = useState<CompletionSoundStatus>(
    getInAppCompletionSoundStatus,
  );
  const [stoppingOperationId, setStoppingOperationId] = useState<string | null>(null);
  const stoppingOperationIdRef = useRef<string | null>(null);
  const [editingTurnId, setEditingTurnId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [replacingTurnId, setReplacingTurnId] = useState<string | null>(null);
  const [branchRevision, setBranchRevision] = useState<number>(0);

  useEffect(() => {
    const updateCompletionSoundStatus = (event: Event) => {
      const status = (event as CustomEvent<CompletionSoundStatus>).detail;
      setCompletionSoundStatus(
        status === 'not-prepared' || status === 'ready' || status === 'needs-activation' || status === 'unavailable'
          ? status
          : getInAppCompletionSoundStatus(),
      );
    };
    window.addEventListener(HOME_CHAT_COMPLETION_SOUND_STATUS_EVENT, updateCompletionSoundStatus);
    return () => window.removeEventListener(HOME_CHAT_COMPLETION_SOUND_STATUS_EVENT, updateCompletionSoundStatus);
  }, []);

  // Tracks the in-flight v2 turn so the progress checklist can subscribe
  // to its server-side progress envelope. Set on submit; cleared on
  // POST resolve/reject. Also rehydrated from `thread.pending_request`
  // on session mount so a mid-flight reload reattaches to the same
  // SSE stream.
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  // Live mirror of `pendingRequestId` for the stale-resolve guard. Async
  // send handlers capture the `crid` they dispatched with; when their
  // fetch later resolves they compare it against `pendingRequestIdRef`.
  // The navigation teardown handlers (select / new-chat / delete) clear
  // `pendingRequestId` (→ this ref becomes a non-matching value), so a
  // late-resolving in-flight turn no-ops its `setPendingClarification` /
  // `setActiveSessionId` / `setTurns` instead of clobbering the pane the
  // user navigated to. Kept in a ref (not read from the state closure)
  // because the resolving closure captured the OLD `pendingRequestId`.
  //
  // The ref is updated SYNCHRONOUSLY everywhere `setPendingRequestId` is
  // called (send dispatch, navigation teardown, progress-complete,
  // adoptPendingRequest) rather than via a `[pendingRequestId]` effect —
  // a mount-time effect carrying the initial `null` could otherwise run
  // AFTER an in-flight dispatch set the ref to its crid, transiently
  // clobbering it to null and tripping the guard on a non-stale resolve.
  const pendingRequestIdRef = useRef<string | null>(null);
  // Any user send invalidates a thread hydration that began before the send.
  // Otherwise a slow GET /thread can replace an optimistic or failed-send
  // bubble with the older server snapshot, making the user's request vanish.
  const interactionRevisionRef = useRef(0);
  const setPendingRequestIdSynced = useCallback(
    (value: string | null) => {
      pendingRequestIdRef.current = value;
      setPendingRequestId(value);
    },
    []
  );
  // Text the user just submitted, held only for the duration of the
  // in-flight turn. Renders as a user bubble immediately above the
  // StepProgressChecklist so the question stays anchored chronologically
  // BELOW any prior turns instead of jumping above them via the legacy
  // `messages` list (which renders before `turns`).
  const [inFlightUserMsg, setInFlightUserMsg] = useState<string | null>(null);
  // Transient clarification state — set only between a vague submit
  // returning `clarification_needed` and its resolution. Holds the
  // suggested rewrites + the ORIGINAL query the client resends alongside
  // `final_question`. On the lazy-create path the envelope's `session_id`
  // tells us which real session to resolve against. NOT persisted; MUST
  // be reset after each answer (§4.1 state hygiene) so a stale
  // `final_question` can never skip the gate on the next turn.
  const [pendingClarification, setPendingClarification] =
    useState<ClarificationEnvelope | null>(null);
  // Thread hydration restores a durable pre-answer clarification card after a
  // user closes and later reopens the session. The backend supplies the
  // exact server-issued envelope; add the selected Home Chat session id so
  // the card's resolve request cannot accidentally lazy-create a new chat.
  const restorePendingClarification = useCallback(
    (thread: { pending_clarification?: ClarificationEnvelope | null }, sessionId: string) => {
      const pending = thread.pending_clarification;
      if (pending && isClarificationEnvelope(pending)) {
        setPendingClarification({ ...pending, session_id: sessionId });
      }
    },
    []
  );
  // Resolved clarification text held for the duration of an in-flight
  // answer reached by resubmitting a `final_question` DIFFERENT from the
  // original query. Renders the same "Clarified: …" callout under the
  // in-flight original-query bubble while the reader/synth agents run.
  // Null for "use my original" and fresh sends. Transient; reset at the
  // §4.1 teardown points so it never leaks into the next turn.
  const [inFlightClarification, setInFlightClarification] = useState<
    string | null
  >(null);
  // Transient post-reader topic-focus state — set only between a broad turn
  // returning `topic_clarification_needed` and its resolution. Holds the
  // clarifying question, candidate topics, original query, and the
  // `focus_token` keying the server-held reader outputs. On the lazy-create
  // path the envelope's `session_id` tells us which real session to resolve
  // against. NOT persisted; MUST be reset after each answer (§4.1 / §9) so a
  // stale `focus_token` can never skip the readers on the next turn.
  const [pendingTopicClarification, setPendingTopicClarification] =
    useState<TopicClarificationEnvelope | null>(null);
  // Clarification cards are distinct successful results, but unlike durable
  // answer completions they are rendered directly from the turn response.
  // Retain dispatch ids for this mounted panel so retries/rerenders cannot
  // repeat the opt-in chime. Thread hydration intentionally never calls this
  // path, keeping restored cards silent.
  const deliveredClarificationSoundIdsRef = useRef<Set<string>>(new Set());
  const maybePlayClarificationSound = useCallback(
    (
      envelope: ClarificationEnvelope | TopicClarificationEnvelope,
      sessionId: string | null | undefined,
      dispatchRequestId?: string,
    ) => {
      // Only the launcher-owned main Home Chat surface delivers sound.
      // WorkflowChatCompanion and standalone workflow embeddings remain
      // silent even when this browser-wide preference is enabled.
      if (!isMainHomeChatSoundSurface) return;
      const soundId = clarificationSoundIdFromEnvelope(
        envelope,
        sessionId,
        dispatchRequestId,
      );
      if (!soundId || deliveredClarificationSoundIdsRef.current.has(soundId)) return;
      deliveredClarificationSoundIdsRef.current.add(soundId);
      try {
        if (window.localStorage.getItem(HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY) === 'true') {
          void tryInAppCompletionSound();
        }
      } catch {
        // Storage is best-effort; an unavailable preference must never block
        // the clarification card itself.
      }
    },
    [isMainHomeChatSoundSurface],
  );
  // The response itself is the lowest-latency point at which this panel can
  // safely know that an actionable result is durable. Hand only its exact
  // server-issued ids to the launcher reconciler; that shared dispatcher
  // owns both audio and page-lifetime poll de-duplication.
  const deliverResponseLlmResultEvents = useCallback((response: unknown) => {
    if (!isMainHomeChatSoundSurface || !response || typeof response !== 'object') return;
    const events = normalizeHomeChatLlmResultEvents(
      (response as { llm_result_events?: unknown }).llm_result_events,
    );
    for (const event of events) dispatchHomeChatLlmResultSound(event.event_id);
  }, [isMainHomeChatSoundSurface]);
  // Chosen focus text held for the duration of an in-flight focused answer,
  // so the "Focused on: …" callout renders immediately while the synth runs.
  // Null for Skip (topic_focus === "") and fresh sends. Transient; reset at
  // the §4.1 teardown points.
  const [inFlightTopicFocus, setInFlightTopicFocus] = useState<string | null>(
    null
  );

  // Used by `+ New chat` to focus the input area. We focus the textarea
  // rendered by MessageInput.
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  // The message list is the only scrollable conversation surface. Keep its
  // scroll ownership local rather than scrolling the panel/window: the input
  // stays pinned, and browser focus is never moved when a message arrives.
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  const [threadScrollRequest, setThreadScrollRequest] = useState({ revision: 0, position: 'bottom' as 'bottom' | 'answer-start' });
  const scrollThreadToLatest = useCallback((position: 'bottom' | 'answer-start' = 'bottom') => {
    setThreadScrollRequest((request) => ({ revision: request.revision + 1, position }));
  }, []);

  // Run after React has committed the user bubble, card, or returned turn to
  // the actual scroll container. This deliberately scrolls only in response
  // to an explicit submit/response request, never on passive history
  // hydration or ordinary re-renders, so reading an older part of a thread is
  // not interrupted. Assigning scrollTop is immediate (and does not steal
  // keyboard focus or force motion on users who prefer reduced motion).
  useLayoutEffect(() => {
    if (threadScrollRequest.revision === 0) return;
    const thread = threadScrollRef.current;
    if (!thread) return;
    const answers = thread.querySelectorAll<HTMLElement>('[data-testid="farchat-response-bubble"]');
    const answer = threadScrollRequest.position === 'answer-start' ? answers[answers.length - 1] : null;
    thread.scrollTop = answer
      ? thread.scrollTop + answer.getBoundingClientRect().top - thread.getBoundingClientRect().top
      : thread.scrollHeight;
  }, [threadScrollRequest]);

  // Launcher-issued guidance follows an explicit new submission. Saved
  // thread hydration never supplies these initial entry props, so it keeps
  // the reader's position when revisiting or passively refreshing history.
  const scrolledInitialEntryId = useRef<string | null>(null);
  useLayoutEffect(() => {
    const cardId = initialWorkflowOnlyPurchaseGuidance?.card_id;
    if (!workflowOnly || !initialWorkflowOnlyMessage || !cardId || scrolledInitialEntryId.current === cardId) return;
    scrolledInitialEntryId.current = cardId;
    scrollThreadToLatest();
  }, [initialWorkflowOnlyPurchaseGuidance?.card_id, initialWorkflowOnlyMessage, scrollThreadToLatest, workflowOnly]);

  // In-flight dedupe for `ensureSession()`. If the user simultaneously
  // types-and-sends and drops a file (two concurrent first-content
  // actions on the pending placeholder), both await the same Promise
  // instead of issuing two POSTs to /sessions.
  const pendingCreatePromiseRef = useRef<Promise<string> | null>(null);

  // Bounded one-shot timer that re-fetches the sessions list mid-turn so the
  // backend's parallel-generated session title surfaces in the rail BEFORE
  // the answer lands. Tracked in a ref so a new send / navigation can cancel
  // a pending fire and so it's cleared on unmount (no setState after unmount).
  const inFlightTitleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Once a turn POST is dispatched, re-read the server-owned activity envelope
  // promptly. The first request can race the POST reaching the server, so a
  // second bounded refresh makes the Stop affordance appear without waiting
  // for the three-second background poll or the title-refresh timer.
  const inFlightActivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Mount-once guard for `initialMessage` / `initialSessionId` priming.
  // Set on the first effect run; never reset. Re-renders with the same
  // (or different) prop values are no-ops. A fresh mount (e.g. dialog
  // close + reopen) creates a fresh ref, allowing re-priming.
  const firstRunRef = useRef(false);

  return {
    constrainedOptimisticSessionId,
    setConstrainedOptimisticSessionId,
    mrObserverNotificationId,
    setMrObserverNotificationId,
    mrNotificationBoundResumeId,
    setMrNotificationBoundResumeId,
    workflowOnlyChoice,
    setWorkflowOnlyChoice,
    workflowOnlyPurchaseGuidance,
    setWorkflowOnlyPurchaseGuidance,
    failedPurchaseFollowup,
    setFailedPurchaseFollowup,
    newPurchaseGuidanceFocusId,
    setNewPurchaseGuidanceFocusId,
    workflowOnlyGuidance,
    setWorkflowOnlyGuidance,
    workflowOnlyEntryMessage,
    setWorkflowOnlyEntryMessage,
    isCompanion,
    isMainHomeChatSoundSurface,
    composerPlaceholder,
    sessions,
    setSessions,
    sessionsLoading,
    setSessionsLoading,
    mobileSessionsOpen,
    setMobileSessionsOpen,
    activeSessionId,
    setActiveSessionId,
    gpcChatView,
    setGpcChatView,
    mrChatView,
    setMrChatView,
    mrTurnInFlight,
    setMrTurnInFlight,
    constructionSowChatView,
    setConstructionSowChatView,
    servicesPwsChatView,
    setServicesPwsChatView,
    workflowDocumentHistory,
    setWorkflowDocumentHistory,
    gpcViewsByHomeSessionRef,
    rehydratingGpcSessionId,
    setRehydratingGpcSessionId,
    optimisticGpcOpening,
    setOptimisticGpcOpening,
    pendingGpcRouter,
    setPendingGpcRouter,
    activeSessionIdRef,
    reportedSessionIdRef,
    sessionsRef,
    sessionListRefreshVersionRef,
    messages,
    setMessages,
    turns,
    setTurns,
    chatCards,
    setChatCards,
    busySimilarRequirementsCardId,
    setBusySimilarRequirementsCardId,
    busyWorkflowRecommendationCardId,
    setBusyWorkflowRecommendationCardId,
    busyActionChoiceLabel,
    setBusyActionChoiceLabel,
    busyTrainingActionId,
    setBusyTrainingActionId,
    workflowRecommendationRequestIdRef,
    cardActionRevisionRef,
    pendingIntentChoiceSelection,
    setPendingIntentChoiceSelection,
    pendingIntentChoiceRequestIdRef,
    clearPendingIntentChoiceSelection,
    conversationItems,
    attachments,
    setAttachments,
    inFlight,
    setInFlight,
    notice,
    setNotice,
    soundEnabled,
    setSoundEnabled,
    completionSoundStatus,
    stoppingOperationId,
    setStoppingOperationId,
    stoppingOperationIdRef,
    editingTurnId,
    setEditingTurnId,
    editDraft,
    setEditDraft,
    replacingTurnId,
    setReplacingTurnId,
    branchRevision,
    setBranchRevision,
    pendingRequestId,
    setPendingRequestId,
    pendingRequestIdRef,
    interactionRevisionRef,
    setPendingRequestIdSynced,
    inFlightUserMsg,
    setInFlightUserMsg,
    pendingClarification,
    setPendingClarification,
    restorePendingClarification,
    inFlightClarification,
    setInFlightClarification,
    pendingTopicClarification,
    setPendingTopicClarification,
    maybePlayClarificationSound,
    deliverResponseLlmResultEvents,
    inFlightTopicFocus,
    setInFlightTopicFocus,
    inputContainerRef,
    threadScrollRef,
    scrollThreadToLatest,
    pendingCreatePromiseRef,
    inFlightTitleTimerRef,
    inFlightActivityTimerRef,
    firstRunRef,
  };
}

export type HomeChatPanelState = ReturnType<typeof useHomeChatPanelState>;
